/**
 * Panel u projektu i instance: build worker, rozdělaná práce, větve.
 * Data jen z MCP toolů — UI nic nedopočítává.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  GitBranch,
  Hammer,
  Loader2,
  GitCommit,
  GitMerge,
  GitPullRequest,
  PauseCircle,
  ChevronDown,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import { themedConfirm, themedPrompt } from '../lib/themed-dialogs';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import {
  driftSeverity,
  driftSummary,
  feVersionSlots,
  prodReleaseId,
  sameReleaseId,
} from '../lib/codable-drift';
import type { CodableDriftStatus } from '../lib/codable-drift';
import { refreshCodableWorkspace } from '../lib/codable-workspace-refresh';
import { askCodableChat } from '../lib/codable-chat-ask';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  StatusDot,
  Textarea,
} from './ui';

export type WorkspaceWorkScope =
  | {
    kind: 'project';
    slug: string;
    gitlabPath?: string | null;
    gitlabProjectKey?: string | null;
    instance?: string | null;
    fleet?: string | null;
  }
  | {
    kind: 'instance';
    instance: string;
    gitlabPath?: string | null;
    fleet?: string | null;
    slug?: string | null;
  };

type CallTool = (
  name: string,
  args: Record<string, unknown>,
  opts?: { silent?: boolean; label?: string },
) => Promise<unknown>;

interface ParkedItem {
  key?: string | null;
  branch?: string | null;
  owner?: string | null;
  file_count?: number | null;
  parked_at?: string | null;
  decision?: string | null;
  defer_note?: string | null;
  mine?: boolean;
  age_days?: number | null;
  commits_behind?: number | null;
  current?: boolean;
  unresolved?: boolean;
  reason?: string | null;
}

interface Graph {
  base?: { name?: string; current?: boolean };
  offshoots?: ParkedItem[];
}

interface DirtyFile {
  status?: string;
  path?: string;
}

interface StatusResp {
  ok?: boolean;
  error?: string;
  dirty?: boolean;
  changed_files?: number;
  files?: DirtyFile[];
  diff?: string | null;
  diff_truncated?: boolean;
  branch?: string | null;
  parked?: ParkedItem[];
  parked_count?: number;
  graph?: Graph;
  base_branch?: string | null;
  gitlab_path?: string | null;
}

interface WorkerResp {
  ok?: boolean;
  worker?: string;
  tag?: string;
  host?: string;
  concurrent?: number | null;
  error?: string;
  remediation?: string;
  hint?: string;
  /**
   * Proč se nestaví, strojově — `none_registered` (nikdo worker nezaregistroval)
   * vs `no_contract` (worker je, tenhle uzel na něj nemá právo). Každý vede na
   * jiné tlačítko; karta to dřív jen vypsala jako větu (Filip 2026-09-13).
   */
  state?: string | null;
  next?: 'ask_admin' | 'request_access' | string | null;
  fleet?: string | null;
}

interface CiResp {
  ok?: boolean;
  changed?: boolean;
  dry_run?: boolean;
  action?: string;
  reason?: string;
  content?: string;
  tag?: string;
  error?: string;
  detail?: string;
  remediation?: string;
  /** Smí volající zapsat do cílové větve? `false` = tlačítko nemá co nabídnout. */
  can_write?: boolean | null;
  write_block?: { error?: string; remediation?: string; protected_branch?: boolean } | null;
}

function behindTone(n: number | null | undefined): 'ok' | 'warn' | 'danger' {
  if (n == null) return 'ok';
  if (n > 30) return 'danger';
  if (n > 10) return 'warn';
  return 'ok';
}

function shortenBranch(name: string): string {
  if (name.length <= 44) return name;
  const parts = name.split('/');
  if (parts.length >= 3) {
    const tail = parts.slice(2).join('/');
    const cut = tail.length > 26 ? `…${tail.slice(-26)}` : tail;
    return `${parts[0]}/${parts[1]}/${cut}`;
  }
  return `${name.slice(0, 18)}…${name.slice(-18)}`;
}

export default function WorkspaceWorkPanel({
  scope,
  compact = false,
  callTool,
}: {
  scope: WorkspaceWorkScope;
  compact?: boolean;
  callTool?: CallTool;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const call: CallTool = callTool ?? ((name, args, opts) => api.mcp(name, args, opts));
  const slug = scope.kind === 'project' ? scope.slug : (scope.slug ?? null);
  const instance = scope.kind === 'instance' ? scope.instance : (scope.instance ?? null);
  const fleet = scope.fleet ?? undefined;
  const statusKey = [
    'workspace-work',
    scope.kind,
    slug ?? '',
    instance ?? '',
  ];



  const statusQ = useQuery({
    queryKey: statusKey,
    queryFn: () => {
      if (scope.kind === 'project') {
        return call('codable.workspace_status', { slug: scope.slug }, { silent: true }) as Promise<
          StatusResp
        >;
      }
      return call('gitlab.repo_status', { instance: scope.instance }, { silent: true }) as Promise<
        StatusResp
      >;
    },
    enabled: scope.kind === 'project' ? !!scope.slug : !!scope.instance,
    staleTime: 12_000,
  });

  const [diffOpen, setDiffOpen] = useState(false);
  const diffQ = useQuery({
    queryKey: [...statusKey, 'diff'],
    queryFn: () =>
      call('codable.workspace_status', { slug, include_diff: true }, { silent: true }) as Promise<
        StatusResp
      >,
    enabled: diffOpen && !!slug,
    staleTime: 4_000,
  });

  const headsQ = useQuery({
    queryKey: ['codable-project-status', slug ?? ''],
    queryFn: () =>
      call('codable.project_status', { slug, refresh_remote: false }, { silent: true }) as Promise<{
        ok?: boolean;
        status?: CodableDriftStatus;
      }>,
    enabled: !!slug,
    staleTime: 8_000,
  });

  const gitlabPath = scope.gitlabPath || statusQ.data?.gitlab_path || null;
  // Klíč vazby drží jen projektový scope — vytažený jednou, ať se narrowing
  // neztratí níž (jinak `scope.gitlabProjectKey` u instance typově neexistuje).
  const gitlabProjectKey = scope.kind === 'project' ? scope.gitlabProjectKey ?? null : null;
  const gitlabBinding = /^([^:\s]+):([1-9][0-9]*)$/.exec(gitlabProjectKey ?? '');
  // Celý klíč vazby (`gitlab:4`), ne rozsekaný na prefix + id: prefix klíče je
  // historický alias a spojení na uzlu se může jmenovat jinak (`gitlab1`).
  // Karta posílala `connection_id: 'gitlab'` → GITLAB_CONNECTION_MISSING a
  // „Znovu ověřit přístup" i „Zapnout build" nedělaly nic (CIEACM000,
  // Filip 2026-09-12). Spojení k klíči zná server (registr gitlab-projects).
  const ciTarget = gitlabBinding ? { project: gitlabProjectKey } : { project: gitlabPath };
  const ciBranch = statusQ.data?.graph?.base?.name || statusQ.data?.base_branch || undefined;
  const workerQ = useQuery({
    queryKey: ['gitlab-worker-resolve', ciTarget.project ?? '', slug ?? '', fleet ?? ''],
    queryFn: () => call('gitlab.worker_resolve', {
      ...(ciTarget.project ? ciTarget : { slug }),
      ...(fleet ? { fleet } : {}),
    }, { silent: true }) as Promise<WorkerResp>,
    enabled: !!ciTarget.project || !!slug,
    staleTime: 30_000,
  });
  const worker = workerQ.data;
  const workerOk = !workerQ.isError && worker?.ok === true && !!worker.tag;
  const ciArgs = {
    ...ciTarget,
    ...(ciBranch ? { branch: ciBranch } : {}),
    ...(fleet ? { fleet } : {}),
  };

  const ciQ = useQuery({
    queryKey: ['gitlab-ci-status', ciTarget, worker?.tag ?? '', ciBranch ?? ''],
    queryFn: () =>
      call('gitlab.ci_enable', {
        ...ciArgs,
        dry_run: true,
      }, { silent: true }) as Promise<CiResp>,
    enabled: workerOk && !!ciTarget.project,
    staleTime: 30_000,
  });

  const status = statusQ.data;
  const parked = status?.parked ?? [];
  const graph = status?.graph;
  const offshoots = graph?.offshoots?.length ? graph.offshoots : parked;
  const unresolvedCount = offshoots.filter((p) => p.unresolved).length;
  const dirty = status?.dirty === true;
  const heads = headsQ.data?.status ?? null;
  const driftLevel = driftSeverity(heads);
  const driftLine = heads ? driftSummary(heads) : null;
  const ciAction = ciQ.data?.action;
  // `gitlab.ci_enable` při už zapnutém CI dřív vracel jen `changed: false`
  // bez `action: 'noop'` — karta pak svítila „zapnout“, i když yaml s tagem
  // v repu byl (keen-comet-3e9f, 2026-08-29).
  const hasCiTag = ciQ.data?.ok === true &&
    (ciAction === 'noop' || (ciQ.data?.changed === false && ciAction !== 'create' &&
      ciAction !== 'update'));
  // Právo zapsat do cílové větve je vlastnost volajícího, ne stav projektu.
  // Bez tohohle karta nabízela tlačítko, které pod chráněnou větví nemohlo
  // uspět, a „znovu ověřit přístup" spouštělo tutéž odmítnutou otázku dokola.
  const canWriteCi = ciQ.data?.can_write !== false;
  const writeBlock = ciQ.data?.write_block ?? null;
  // Selhání dotazu na CI není „projekt nestaví na workeru" — je to neověřený
  // stav. Dřív se z chyby routingu udělala žlutá karta s tlačítkem Zapnout,
  // které padalo na tutéž chybu.
  const ciCheckFailed = workerOk && !ciQ.isLoading && (ciQ.isError || ciQ.data?.ok === false);
  const workerState: 'green' | 'yellow' | 'gray' | 'loading' | 'unknown' =
    workerQ.isLoading || (workerOk && ciQ.isLoading)
    ? 'loading'
    : !workerOk
    ? 'gray'
    : ciCheckFailed
    ? 'unknown'
    : hasCiTag
    ? 'green'
    : 'yellow';

  const attention = workerState === 'yellow' || workerState === 'gray' ||
    workerState === 'unknown' ||
    unresolvedCount > 0 || dirty || driftLevel !== 'ok';
  const [open, setOpen] = useState(false);
  const expanded = compact ? true : open;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [enableError, setEnableError] = useState<string | null>(null);
  const [deferFor, setDeferFor] = useState<ParkedItem | null>(null);
  const [deferNote, setDeferNote] = useState('');

  const invalidate = (opts?: { preview?: boolean }) => {
    refreshCodableWorkspace(qc, { slug, preview: opts?.preview });
  };

  /**
   * „Znovu ověřit přístup" musí ověřit i tehdy, když je dotaz vypnutý.
   *
   * Původně to volalo `ciQ.refetch()`. Jenže ten dotaz běží jen s dostupným
   * workerem (`enabled`), a vypnutý dotaz se v React Query 5 refetchem
   * nespustí — kliknutí tedy neudělalo vůbec nic (Filip 2026-09-10). Ověření
   * je akce uživatele, ne obnova cache, takže se ptá napřímo a výsledek řekne.
   */
  const recheckMut = useMutation({
    mutationFn: async () => {
      setEnableError(null);
      const r = await call('gitlab.ci_enable', {
        ...ciArgs,
        dry_run: true,
      }, { silent: true }) as CiResp;
      qc.setQueryData(['gitlab-ci-status', ciTarget, worker?.tag ?? '', ciBranch ?? ''], r);
      invalidate({ preview: false });
      return r;
    },
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.message([r.error, r.remediation].filter(Boolean).join(' ') ||
          t('workspaceWork.recheckStillBlocked', 'Přístup se zatím nepovedlo ověřit.'));
      } else if (r?.can_write === false) {
        toast.message(String(r.write_block?.remediation ?? '') ||
          t('workspaceWork.recheckReadOnly', 'Čteme, ale zapisovat do větve nemůžeš.'));
      } else {
        toast.success(t('workspaceWork.recheckOk', 'Přístup ověřen.'));
      }
    },
    onError: (e) => setEnableError((e as Error).message),
  });

  const enableMut = useMutation({
    onMutate: () => setEnableError(null),
    mutationFn: () =>
      call('gitlab.ci_enable', {
        ...ciArgs,
      }, { label: t('workspaceWork.enableBusy', 'Zapínám build na workeru') }) as Promise<CiResp>,
    onSuccess: (r) => {
      if (r?.ok === false) {
        setEnableError([r.error, r.remediation].filter(Boolean).join(' ') ||
          t('workspaceWork.enableFailed', 'Zapnutí selhalo'));
        invalidate({ preview: false });
        return;
      }
      if (r?.changed) {
        toast.success(t('workspaceWork.enabled', 'Zapnuto, pipeline se spustí při dalším commitu'));
      } else {
        toast.message(t('workspaceWork.alreadyOn', 'Už bylo zapnuté'));
      }
      invalidate({ preview: false });
    },
    onError: (e: unknown) => setEnableError(String((e as Error).message ?? e)),
  });

  /**
   * Cesta ven z „Není kde stavět" (Filip 2026-09-13).
   *
   * Dvě různé situace, dvě různá tlačítka — rozlišuje je `next` z
   * `gitlab.worker_resolve`, ne hádání z textu: `request_access` = worker
   * existuje a chybí kontrakt, `ask_admin` = worker neexistuje vůbec a musí ho
   * zaregistrovat správce platformy.
   */
  const accessRequestMut = useMutation({
    mutationFn: () =>
      call('gitlab.worker_access_request', {
        worker: worker?.worker,
        ...(slug ? { reason: t('workspaceWork.requestReason', 'Projekt {{slug}} potřebuje build.', { slug }) } : {}),
      }, { label: t('workspaceWork.requestAccessBusy', 'Odesílám žádost') }) as Promise<{
        ok?: boolean;
        state?: string;
        code?: string;
        error?: string;
        remediation?: string;
      }>,
    onSuccess: () => workerQ.refetch(),
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
  });

  const workerRequestMut = useMutation({
    mutationFn: () =>
      call('gitlab.worker_request', {
        ...(slug ? { slug } : {}),
        ...(gitlabPath ? { project: gitlabPath } : {}),
        kind: 'node',
      }, { label: t('workspaceWork.requestAdminBusy', 'Odesílám žádost správci') }) as Promise<{
        ok?: boolean;
        state?: string;
        reused?: boolean;
        notified?: number;
        warning?: string;
        error?: string;
      }>,
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
  });

  const decideMut = useMutation({
    mutationFn: (body: {
      branch: string;
      decision: 'merge' | 'rebase' | 'defer';
      defer_note?: string;
      parked_key?: string;
    }) =>
      call('apphost.workspace_decide', {
        ...body,
        ...(instance ? { instance } : {}),
        ...(slug ? { slug } : {}),
        base_branch: graph?.base?.name || status?.base_branch || 'main',
      }, { label: t('workspaceWork.decideBusy', 'Provádím rozhodnutí') }) as Promise<{
        ok?: boolean;
        error?: string;
        actions?: string[];
        conflicts?: string[];
        branch?: string;
        decision?: string;
        skip_reset?: boolean;
      }>,
    onSuccess: (r) => {
      if (r?.ok === false) {
        toast.error(r.error || t('workspaceWork.decideFailed', 'Rozhodnutí selhalo'));
        return;
      }
      if (r.decision === 'defer' && r.branch && r.skip_reset === false) {
        toast.success(
          t('workspaceWork.parkOk', 'Odloženo do {{branch}}', { branch: r.branch }),
        );
      } else {
        toast.success((r.actions ?? []).join(' · ') || t('workspaceWork.decideOk', 'Hotovo'));
      }
      setDeferFor(null);
      setDeferNote('');
      invalidate();
    },
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
  });

  const commitMut = useMutation({
    mutationFn: (message: string) => {
      if (!slug) throw new Error(t('workspaceWork.noSlug', 'Commit je u projektu'));
      return call('codable.commit', { slug, message }, {
        label: t('workspaceWork.commitBusy', 'Commit'),
      }) as Promise<{ ok?: boolean; pushed?: boolean; error?: string }>;
    },
    onSuccess: (r) => {
      // Výsledek nástroje není úspěch jen tím, že přišel: commit mohl zůstat
      // lokálně (push bez pověření) a UI hlásilo „Zapsáno" (newpluto 2026-10-17).
      if (r?.ok === false || r?.pushed === false) {
        toast.error(r?.error || t('workspaceWork.commitNotPushed', 'Commit zůstal jen ve workspace — push do GitLabu neprošel.'));
      } else {
        toast.success(t('workspaceWork.commitOk', 'Zapsáno'));
      }
      invalidate();
    },
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
  });

  const fmtWhen = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(i18n.language === 'cvm' ? 'cs-CZ' : i18n.language, {
      day: 'numeric',
      month: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const askCommit = async () => {
    // Návrh zprávy ze změněných souborů — prázdné pole nutilo vymýšlet název
    // (Filip 2026-10-17: „nenavrhlo mi to žádný název, to není hezké").
    const names = (status?.files ?? [])
      .map((f) => (f.path ?? '').split('/').pop() ?? '')
      .filter(Boolean);
    const shown = names.slice(0, 3).join(', ');
    const more = names.length > 3 ? ` (+${names.length - 3})` : '';
    const suggested = names.length
      ? t('workspaceWork.commitSuggested', 'Úpravy: {{files}}{{more}}', { files: shown, more })
      : '';
    const msg = await themedPrompt({
      title: t('workspaceWork.commitPrompt', 'Zpráva commitu'),
      label: t('workspaceWork.commitLabel', 'Commit'),
      defaultValue: suggested,
      allowEmpty: false,
    });
    if (!msg?.trim()) return;
    commitMut.mutate(msg.trim());
  };

  const discardMut = useMutation({
    mutationFn: (to: 'head' | 'origin' = 'head') => {
      if (!slug) throw new Error(t('workspaceWork.noSlug', 'Zahodit jde u projektu'));
      return call('codable.workspace_discard', { slug, confirm: true, to }, {
        label: to === 'origin'
          ? t('workspaceWork.alignBusy', 'Zarovnávám na Git')
          : t('workspaceWork.discardBusy', 'Zahazuji změny'),
      }) as Promise<
        { ok?: boolean; error?: string; discarded?: number; dropped_commits?: number }
      >;
    },
    onSuccess: (r, to) => {
      if (r?.ok === false) {
        toast.error(r.error || t('workspaceWork.discardFailed', 'Zahození selhalo'));
        return;
      }
      toast.success(
        to === 'origin'
          ? t('workspaceWork.alignOk', 'Workspace zarovnaný na Git ({{count}} commitů zahozeno)', {
            count: r?.dropped_commits ?? 0,
          })
          : t('workspaceWork.discardOk', 'Nezapsané změny zahozeny'),
      );
      invalidate();
    },
    onError: (e: unknown) => toast.error(String((e as Error).message ?? e)),
  });

  const askDiscard = async () => {
    const ok = await themedConfirm({
      title: t('workspaceWork.discardTitle', 'Zahodit nezapsané změny?'),
      description: t(
        'workspaceWork.discardDesc',
        'Zmizí {{count}} nezapsaných změn ve workspace. Nejde vrátit.',
        { count: status?.changed_files ?? 0 },
      ),
      confirmLabel: t('workspaceWork.discard', 'Zahodit'),
      danger: true,
    });
    if (!ok) return;
    discardMut.mutate('head');
  };

  /**
   * Zahodit i lokální commity a srovnat se na GitLab.
   *
   * Filip 2026-09-13: „nemám nástroj na git reset --hard." Workspace napřed
   * proti originu (keen-comet 4 commity) se jinak nedal srovnat než ručně na
   * uzlu — a odložení na větev (`park`) je pro případ, kdy si tu práci chceš
   * nechat. Tohle je ta druhá odpověď: nechci.
   */
  const askAlign = async () => {
    const ahead = heads?.dev?.ahead ?? 0;
    const ok = await themedConfirm({
      title: t('workspaceWork.alignTitle', 'Zarovnat workspace na Git?'),
      description: t(
        'workspaceWork.alignDesc',
        'Zmizí {{count}} lokálních commitů i nezapsané změny. Zůstane přesně to, co je v GitLabu. Nejde vrátit.',
        { count: ahead },
      ),
      confirmLabel: t('workspaceWork.alignConfirm', 'Zarovnat na Git'),
      danger: true,
    });
    if (!ok) return;
    discardMut.mutate('origin');
  };

  const askPark = async () => {
    const branch = status?.branch?.trim();
    if (!branch) {
      toast.error(t('workspaceWork.parkNoBranch', 'Neznám aktuální větev'));
      return;
    }
    const note = await themedPrompt({
      title: t('workspaceWork.parkTitle', 'Odložit na novou větev'),
      description: t(
        'workspaceWork.parkDesc',
        'Nezapsané změny půjdou do větve wip/… a workspace se vrátí na {{branch}}.',
        { branch },
      ),
      label: t('workspaceWork.parkNote', 'Na co se čeká'),
      placeholder: t('workspaceWork.parkNotePh', 'např. až bude review'),
      allowEmpty: false,
      confirmLabel: t('workspaceWork.parkConfirm', 'Odložit'),
    });
    if (!note?.trim()) return;
    decideMut.mutate({
      branch,
      decision: 'defer',
      defer_note: note.trim(),
    });
  };

  const askExplain = () => {
    if (!slug) {
      toast.error(t('workspaceWork.chatNeedProject', 'Chat je u Codable projektu'));
      return;
    }
    const files = (diffQ.data?.files ?? status?.files ?? [])
      .map((f) => f.path)
      .filter((p): p is string => !!p);
    askCodableChat({
      slug,
      text: t(
        'workspaceWork.explainPrompt',
        'Vysvětli nezapsané změny v projektu {{slug}} na větvi {{branch}}. Je jich {{count}}: {{files}}. Nejdřív zavolej codable.workspace_status se slugem a include_diff true. Nic nezapisuj ani necommituj — jen vysvětli, co se změnilo a k čemu to je.',
        {
          slug,
          branch: status?.branch ?? '—',
          count: files.length || status?.changed_files || 0,
          files: files.slice(0, 24).join(', ') || '—',
        },
      ),
    });
    setDiffOpen(false);
  };

  const askMergeHelp = (item: ParkedItem) => {
    if (!slug) {
      toast.error(t('workspaceWork.chatNeedProject', 'Chat je u Codable projektu'));
      return;
    }
    const branch = item.branch?.trim();
    if (!branch) return;
    const base = graph?.base?.name || status?.base_branch || 'main';
    askCodableChat({
      slug,
      text: t(
        'workspaceWork.mergeHelpPrompt',
        'Pomoz sloučit odloženou větev {{branch}} do {{base}} v projektu {{slug}}. Poznámka: {{note}}. Nejdřív se podívej na stav (codable.workspace_status). Slití přes apphost.workspace_decide {slug, branch, decision: merge}. Když jsou konflikty, vysvětli je a zeptej se. Práci nezahazuj.',
        {
          slug,
          branch,
          base,
          note: item.defer_note || item.reason || '—',
        },
      ),
    });
  };

  const runDecide = (item: ParkedItem, decision: 'merge' | 'rebase' | 'defer', note?: string) => {
    const branch = item.branch?.trim();
    if (!branch) return;
    decideMut.mutate({
      branch,
      decision,
      ...(note ? { defer_note: note } : {}),
      ...(item.key && !item.key.startsWith('live:') ? { parked_key: item.key } : {}),
    });
  };

  const sha = (label: string, value?: string | null, lit?: boolean) => (
    <span className='flex items-baseline gap-1.5'>
      <span className={cn(
        'shrink-0 text-[10px] font-medium uppercase tracking-wide',
        lit ? 'text-emerald-400' : 'text-gray-600',
      )}>
        {label}
      </span>
      <span className={cn(
        'w-[8ch] font-mono tabular-nums text-[11px]',
        lit ? 'text-emerald-400' : 'text-gray-400',
      )}>
        {(value ?? '').slice(0, 8) || '--------'}
      </span>
    </span>
  );

  const versionSlotLabel: Record<'dev' | 'preview' | 'reserved', string> = {
    dev: t('workspaceWork.buildDev', 'DEV'),
    preview: t('workspaceWork.buildPreview', 'náhled'),
    reserved: t('workspaceWork.buildReserved', 'záloha'),
  };
  const versionSlotValue = (slot: 'dev' | 'preview' | 'reserved') => {
    if (slot === 'dev') return heads?.dev?.short_head ?? null;
    if (slot === 'preview') return heads?.carousel?.preview ?? null;
    return heads?.carousel?.reserved ?? null;
  };
  const versionSlots = heads
    ? feVersionSlots(heads.topology, heads.carousel)
    : [];
  const prodId = prodReleaseId(heads?.carousel, heads?.prod?.short_head);
  const devProdSame = sameReleaseId(heads?.dev?.short_head, prodId);
  const headsBits = versionSlots.length > 0
    ? (
      <span className='hidden shrink-0 items-center gap-3 sm:flex'>
        {versionSlots.map((slot) => {
          const value = versionSlotValue(slot);
          const lit = devProdSame && sameReleaseId(value, prodId);
          return (
            <span key={slot}>{sha(versionSlotLabel[slot], value, lit)}</span>
          );
        })}
      </span>
    )
    : null;

  const stripMeta = (
    <>
      {unresolvedCount > 0 && (
        <Badge variant='error' className='text-[10px]'>
          {t('workspaceWork.unresolvedCount', '{{count}} nerozhodnuto', { count: unresolvedCount })}
        </Badge>
      )}
      {dirty && (
        <Badge variant='warning' className='text-[10px]'>
          {t('workspaceWork.dirtyBadge', '{{count}} nezapsaných', { count: status?.changed_files ?? 0 })}
        </Badge>
      )}
      {offshoots.length > 0 && unresolvedCount === 0 && (
        <span className='text-[11px] text-gray-500'>
          {t('workspaceWork.parkedQuiet', '{{count}} odložených', { count: offshoots.length })}
        </span>
      )}
    </>
  );

  const strip = (
    <button
      type='button'
      onClick={() => setOpen(!expanded)}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-2 text-left text-xs',
        unresolvedCount > 0 || driftLevel === 'danger'
          ? 'bg-red-950/20 text-red-100'
          : attention
          ? 'bg-amber-950/20 text-amber-100'
          : 'bg-gray-950/50 text-gray-400',
      )}
    >
      {expanded ? <ChevronDown size={14} className='shrink-0' /> : <ChevronRight size={14} className='shrink-0' />}
      <span
        className={cn(
          'rounded-full w-2 h-2 shrink-0',
          unresolvedCount > 0 || driftLevel === 'danger'
            ? 'bg-red-400 animate-pulse'
            : attention
            ? 'bg-amber-400 animate-pulse'
            : workerState === 'green'
            ? 'bg-green-400'
            : 'bg-gray-500',
        )}
        aria-hidden
      />
      <span className={cn(
        'min-w-0 flex-1 truncate text-xs font-medium',
        unresolvedCount > 0 || driftLevel === 'danger'
          ? 'text-red-300'
          : attention
          ? 'text-amber-200'
          : 'text-gray-400',
      )}>
        {driftLine || t('workspaceWork.workerTitle', 'Build worker')}
      </span>
      <span className='flex shrink-0 items-center gap-3'>
        {stripMeta}
        {headsBits}
        <span className='w-[7.5rem] text-right text-[10px] uppercase tracking-wide text-gray-600'>
          {expanded
            ? t('workspaceWork.hide', 'Skrýt')
            : t('workspaceWork.show', 'Ukázat práci')}
        </span>
      </span>
    </button>
  );

  const workerCard = (
    <Card className={cn(
      'min-w-0',
      workerState === 'green' && 'ring-1 ring-emerald-500/25',
      (workerState === 'yellow' || workerState === 'unknown') && 'ring-1 ring-amber-500/30',
    )}>
      <CardHeader className='py-2'>
        <CardTitle icon={Hammer}>{t('workspaceWork.workerTitle', 'Build worker')}</CardTitle>
      </CardHeader>
      <CardContent className='p-4 space-y-3'>
        {workerQ.isLoading ? (
          <div className='flex items-center gap-2 text-xs text-gray-500'>
            <Loader2 size={14} className='animate-spin' />
            {t('workspaceWork.loading', 'Načítám…')}
          </div>
        ) : workerState === 'green' ? (
          <div className='space-y-1'>
            <StatusDot status='running' withLabel label={t('workspaceWork.workerOn', 'Build na workeru')} />
            <div className='text-xs text-gray-400 font-mono'>{worker?.tag}</div>
            {worker?.concurrent != null && (
              <div className='text-xs text-gray-500'>
                {t('workspaceWork.concurrent', '{{count}} jobů paralelně', { count: worker.concurrent })}
              </div>
            )}
            {worker?.host && <div className='text-[11px] text-gray-600 font-mono'>{worker.host}</div>}
          </div>
        ) : workerState === 'unknown' ? (
          <div className='space-y-3'>
            <StatusDot
              status='pending'
              withLabel
              label={t('workspaceWork.ciUnknown', 'Stav CI se nepodařilo ověřit')}
            />
            <Alert variant='warning'>
              <div role='alert' className='text-xs whitespace-pre-wrap'>
                {(ciQ.error as Error | null)?.message ||
                  [ciQ.data?.error, ciQ.data?.remediation, ciQ.data?.detail].filter(Boolean).join(' ') ||
                  t('workspaceWork.ciUnknownHint', 'GitLab na dotaz neodpověděl. Zkus to znovu.')}
              </div>
              <button
                type='button'
                className='text-xs underline mt-2 disabled:no-underline disabled:opacity-60'
                disabled={recheckMut.isPending}
                onClick={() => recheckMut.mutate()}
              >
                {recheckMut.isPending
                  ? t('workspaceWork.recheckBusy', 'Ověřuji…')
                  : t('workspaceWork.recheckAccess', 'Znovu ověřit přístup')}
              </button>
            </Alert>
          </div>
        ) : workerState === 'yellow' ? (
          <div className='space-y-3'>
            <StatusDot status='pending' withLabel label={t('workspaceWork.workerLocal', 'Build běží na uzlu aplikace')} />
            <p className='text-xs text-amber-200/90'>
              {t('workspaceWork.workerLocalHint', 'Worker je, ale projekt ještě nestaví na něm.')}
            </p>
            {gitlabPath ? (
              <div className='flex flex-col items-start gap-2'>
                {canWriteCi ? (
                  <Button
                    size='sm'
                    variant='warning'
                    loading={enableMut.isPending}
                    onClick={() => enableMut.mutate()}
                  >
                    {t('workspaceWork.enable', 'Zapnout build na workeru')}
                  </Button>
                ) : (
                  <p className='text-xs text-amber-200/90 whitespace-pre-wrap'>
                    {String(writeBlock?.remediation ?? '') ||
                      t(
                        'workspaceWork.enableNoRight',
                        'Zapnout to musí někdo s právem zápisu do cílové větve.',
                      )}
                  </p>
                )}
                <button
                  type='button'
                  className='text-[11px] leading-relaxed text-plex hover:underline'
                  onClick={() => setPreviewOpen((v) => !v)}
                >
                  {t('workspaceWork.showYaml', 'ukázat, co se zapíše')}
                </button>
              </div>
            ) : (
              <p className='text-xs text-gray-500'>
                {t('workspaceWork.noGitlab', 'Projekt nemá GitLab cestu — worker nejde zapnout.')}
              </p>
            )}
            {previewOpen && (
              <pre className='max-h-56 overflow-auto rounded-lg bg-black/40 p-3 text-[10px] text-gray-300 font-mono leading-relaxed'>
                {ciQ.isLoading
                  ? t('workspaceWork.loading', 'Načítám…')
                  : (ciQ.data?.content || ciQ.data?.error || '—')}
              </pre>
            )}
            {(enableError || ciQ.isError || ciQ.data?.ok === false) && (
              <Alert variant='warning'>
                <div role='alert' className='text-xs whitespace-pre-wrap'>
                  {enableError || (ciQ.error as Error | null)?.message ||
                    [ciQ.data?.error, ciQ.data?.remediation].filter(Boolean).join(' ')}
                </div>
                <button
                  type='button'
                  className='text-xs underline mt-2 disabled:no-underline disabled:opacity-60'
                  disabled={recheckMut.isPending || enableMut.isPending}
                  onClick={() => recheckMut.mutate()}
                >
                  {recheckMut.isPending
                    ? t('workspaceWork.recheckBusy', 'Ověřuji…')
                    : t('workspaceWork.recheckAccess', 'Znovu ověřit přístup')}
                </button>
              </Alert>
            )}
          </div>
        ) : (
          <div className='space-y-2'>
            <StatusDot status='stopped' withLabel label={t('workspaceWork.workerNone', 'Není kde stavět')} />
            <p className='text-xs text-gray-400 whitespace-pre-wrap'>
              {worker?.remediation || worker?.hint || worker?.error ||
                (workerQ.error as Error | undefined)?.message ||
                t('workspaceWork.workerNoneHint', 'Požádej správce flotily o registraci workeru.')}
            </p>
            <Link className='text-xs underline' to='/modules/gitlab?tab=workers'>
              {t('gitlab.tabs.workers', 'Workers')}
            </Link>
            {/* Worker existuje, chybí jen povolení — žádost jde na schválení kontraktu. */}
            {worker?.next === 'request_access' && !!worker?.worker && (
              <>
                <Button
                  variant='secondary'
                  size='sm'
                  disabled={accessRequestMut.isPending}
                  onClick={() => accessRequestMut.mutate()}
                >
                  {accessRequestMut.isPending
                    ? t('workspaceWork.requestAccessBusy', 'Odesílám žádost')
                    : t('workspaceWork.requestAccess', 'Požádat o přístup k workeru')}
                </Button>
                {accessRequestMut.data?.state === 'pending_approval' && (
                  <p className='text-xs text-gray-400'>
                    {t('workspaceWork.requestAccessPending', 'Žádost čeká na schválení správce.')}
                  </p>
                )}
                {accessRequestMut.data?.state === 'granted' && (
                  <p className='text-xs text-emerald-400'>
                    {t('workspaceWork.requestAccessGranted', 'Přístup udělen — zkus build zapnout znovu.')}
                  </p>
                )}
                {accessRequestMut.data?.ok === false && (
                  <Alert variant='warning'>
                    <div role='alert' className='text-xs whitespace-pre-wrap'>
                      {[accessRequestMut.data.error, accessRequestMut.data.remediation]
                        .filter(Boolean).join(' ') ||
                        t('workspaceWork.requestAccessFailed', 'Žádost se nepodařilo založit.')}
                    </div>
                  </Alert>
                )}
              </>
            )}
            {/* Žádný worker neexistuje — zaregistrovat ho může jen správce platformy. */}
            {(worker?.next === 'ask_admin' || worker?.state === 'none_registered') && (
              <>
                <Button
                  variant='secondary'
                  size='sm'
                  disabled={workerRequestMut.isPending || workerRequestMut.data?.ok === true}
                  onClick={() => workerRequestMut.mutate()}
                >
                  {workerRequestMut.isPending
                    ? t('workspaceWork.requestAdminBusy', 'Odesílám žádost správci')
                    : t('workspaceWork.requestAdmin', 'Požádat správce o worker')}
                </Button>
                {workerRequestMut.data?.ok === true && (
                  <p className='text-xs text-gray-400'>
                    {workerRequestMut.data.reused
                      ? t('workspaceWork.requestAdminReused', 'Žádost už běží — správce ji má.')
                      : t('workspaceWork.requestAdminSent', 'Žádost odeslána správci.')}
                  </p>
                )}
                {!!workerRequestMut.data?.warning && (
                  <Alert variant='warning'>
                    <span className='text-xs whitespace-pre-wrap'>{workerRequestMut.data.warning}</span>
                  </Alert>
                )}
              </>
            )}
          </div>
        )}
        {enableMut.data?.ok === false && enableMut.data.remediation && (
          <Alert variant='warning'>
            <span className='text-xs whitespace-pre-wrap'>{enableMut.data.remediation}</span>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const parkedCard = (
    <Card className={cn('min-w-0', unresolvedCount > 0 && 'ring-1 ring-red-500/35')}>
      <CardHeader className='py-2'>
        <CardTitle icon={GitCommit}>{t('workspaceWork.parkedTitle', 'Rozdělaná práce')}</CardTitle>
      </CardHeader>
      <CardContent className='p-4 space-y-3'>
        {statusQ.isLoading ? (
          <div className='flex items-center gap-2 text-xs text-gray-500'>
            <Loader2 size={14} className='animate-spin' />
            {t('workspaceWork.loading', 'Načítám…')}
          </div>
        ) : (
          <>
            {dirty && (
              <Alert variant='warning'>
                <div className='flex flex-wrap items-center justify-between gap-2'>
                  <span className='text-xs'>
                    {t('workspaceWork.dirtyLine', 've workspace je {{count}} nezapsaných změn', {
                      count: status?.changed_files ?? 0,
                    })}
                  </span>
                  {slug && (
                    <span className='flex items-center gap-3'>
                      <button
                        type='button'
                        className='text-[11px] font-medium text-plex hover:underline disabled:opacity-40'
                        onClick={() => setDiffOpen(true)}
                      >
                        {t('workspaceWork.viewLink', 'zobrazit změny')}
                      </button>
                      <button
                        type='button'
                        className='text-[11px] font-medium text-plex hover:underline disabled:opacity-40'
                        disabled={commitMut.isPending || discardMut.isPending}
                        onClick={() => void askCommit()}
                      >
                        {t('workspaceWork.commitLink', 'commit')}
                      </button>
                      <button
                        type='button'
                        className='text-[11px] font-medium text-plex hover:underline disabled:opacity-40'
                        disabled={commitMut.isPending || discardMut.isPending || decideMut.isPending}
                        onClick={() => void askPark()}
                      >
                        {t('workspaceWork.parkLink', 'odložit na novou větev')}
                      </button>
                      <button
                        type='button'
                        className='text-[11px] font-medium text-red-400 hover:underline disabled:opacity-40'
                        disabled={commitMut.isPending || discardMut.isPending || decideMut.isPending}
                        onClick={() => void askDiscard()}
                      >
                        {t('workspaceWork.discard', 'zahodit')}
                      </button>
                    </span>
                  )}
                </div>
              </Alert>
            )}
            {offshoots.length === 0 && !dirty && (
              <p className='text-xs text-gray-500'>
                {t('workspaceWork.parkedEmpty', 'Nic odloženého — práce je v aktuální větvi.')}
              </p>
            )}
            {/* Workspace napřed proti GitLabu: buď si tu práci nechat (odložit
                na větev, commit), nebo ji zahodit. Druhá možnost dosud nebyla
                (Filip 2026-09-13: „nemám nástroj na git reset --hard"). */}
            {(heads?.dev?.ahead ?? 0) > 0 && (
              <div className='flex flex-wrap items-center gap-3 text-xs'>
                <span className='text-amber-200'>
                  {t('workspaceWork.aheadLocal', 'Workspace má {{count}} commitů, které v GitLabu nejsou.', {
                    count: heads?.dev?.ahead ?? 0,
                  })}
                </span>
                <button
                  type='button'
                  className='text-[11px] font-medium text-red-400 hover:underline disabled:opacity-40'
                  disabled={discardMut.isPending || commitMut.isPending}
                  onClick={() => void askAlign()}
                >
                  {t('workspaceWork.alignLink', 'zarovnat na Git')}
                </button>
              </div>
            )}
            <ul className='space-y-2'>
              {offshoots.map((item, idx) => (
                <ParkedRow
                  key={item.key || item.branch || idx}
                  item={item}
                  fmtWhen={fmtWhen}
                  onMergeHelp={slug ? () => askMergeHelp(item) : undefined}
                />
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );

  const graphCard = (
    <Card className='min-w-0'>
      <CardHeader className='py-2'>
        <CardTitle icon={GitBranch}>{t('workspaceWork.graphTitle', 'Větve')}</CardTitle>
      </CardHeader>
      <CardContent className='p-4'>
        <BranchGraphView
          baseName={graph?.base?.name || status?.base_branch || 'main'}
          baseCurrent={graph?.base?.current !== false && !offshoots.some((o) => o.current)}
          offshoots={offshoots}
          compact={compact}
          busy={decideMut.isPending}
          onMerge={(item) => runDecide(item, 'merge')}
          onRebase={(item) => runDecide(item, 'rebase')}
          onDefer={(item) => {
            setDeferFor(item);
            setDeferNote('');
          }}
          onMergeHelp={slug ? askMergeHelp : undefined}
        />
      </CardContent>
    </Card>
  );

  return (
    <div className={cn('border-b border-gray-800/80', attention && 'border-amber-500/20')}>
      {!compact && strip}
      {expanded && (
        <div className={cn(
          'px-4 py-3 space-y-3',
          compact ? 'px-0 py-0 space-y-2' : '',
        )}>
          {compact
            ? (
              <div className='grid grid-cols-1 gap-3'>
                {(driftLine || heads) && (
                  <div className='flex items-center gap-3 px-1 text-xs'>
                    <span className={cn(
                      'min-w-0 flex-1 truncate font-medium',
                      driftLevel === 'danger' ? 'text-red-300' : driftLevel === 'warn' ? 'text-amber-200' : 'text-gray-500',
                    )}>
                      {driftLine}
                    </span>
                    {headsBits}
                  </div>
                )}
                {graphCard}
                {parkedCard}
                {workerCard}
              </div>
            )
            : (
              <>
                <div className='grid gap-3 grid-cols-1 lg:grid-cols-2'>
                  {workerCard}
                  {parkedCard}
                </div>
                {graphCard}
              </>
            )}
        </div>
      )}

      <Modal
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        title={t('workspaceWork.diffTitle', 'Nezapsané změny')}
        size='xl'
        footer={slug
          ? (
            <div className='flex justify-end'>
              <Button
                size='sm'
                variant='secondary'
                icon={<MessageSquare size={14} />}
                onClick={() => askExplain()}
              >
                {t('workspaceWork.explainLink', 'Vysvětlit')}
              </Button>
            </div>
          )
          : undefined}
      >
        <DirtyChangesView
          files={diffQ.data?.files ?? status?.files ?? []}
          diff={diffQ.data?.diff ?? null}
          truncated={diffQ.data?.diff_truncated === true}
          loading={diffQ.isFetching}
          ready={diffQ.isSuccess}
          error={diffQ.isError ? String((diffQ.error as Error)?.message ?? diffQ.error) : null}
        />
      </Modal>

      <Modal
        open={!!deferFor}
        onClose={() => setDeferFor(null)}
        title={t('workspaceWork.deferTitle', 'Ponechat odložené')}
        size='md'
        footer={
          <div className='flex justify-end gap-2'>
            <Button size='sm' variant='ghost' onClick={() => setDeferFor(null)}>
              {t('ui.cancel', 'Zrušit')}
            </Button>
            <Button
              size='sm'
              variant='warning'
              disabled={!deferNote.trim()}
              loading={decideMut.isPending}
              onClick={() => {
                if (!deferFor || !deferNote.trim()) return;
                runDecide(deferFor, 'defer', deferNote.trim());
              }}
            >
              {t('workspaceWork.deferConfirm', 'Ponechat odložené')}
            </Button>
          </div>
        }
      >
        <p className='text-xs text-gray-400 mb-2'>
          {t(
            'workspaceWork.deferHint',
            'Bez poznámky, NA CO se čeká, odložení nepokračuje. Prázdné odložení dělá hřbitov.',
          )}
        </p>
        {deferFor?.branch && (
          <div className='mb-2 font-mono text-[11px] text-gray-500'>{deferFor.branch}</div>
        )}
        <Textarea
          size='sm'
          value={deferNote}
          onChange={(e) => setDeferNote(e.target.value)}
          placeholder={t('workspaceWork.deferPlaceholder', 'čeká na dodělání…')}
        />
      </Modal>
    </div>
  );
}

function fileKind(status: string | undefined): 'new' | 'deleted' | 'renamed' | 'changed' {
  const s = (status ?? '').replace(/\s/g, '');
  if (s === '??' || s.includes('A')) return 'new';
  if (s.includes('D')) return 'deleted';
  if (s.includes('R')) return 'renamed';
  return 'changed';
}

function DirtyChangesView({
  files,
  diff,
  truncated,
  loading,
  ready,
  error,
}: {
  files: DirtyFile[];
  diff: string | null;
  truncated: boolean;
  loading: boolean;
  ready: boolean;
  error: string | null;
}) {
  const { t } = useTranslation();
  return (
    <div className='space-y-3'>
      {files.length > 0 && (
        <ul className='max-h-36 overflow-auto rounded-lg border border-gray-800/80 bg-gray-950/40 px-3 py-2 space-y-1'>
          {files.map((f) => {
            const kind = fileKind(f.status);
            const label = kind === 'new'
              ? t('workspaceWork.fileNew', 'nový')
              : kind === 'deleted'
              ? t('workspaceWork.fileDeleted', 'smazaný')
              : kind === 'renamed'
              ? t('workspaceWork.fileRenamed', 'přejmenovaný')
              : t('workspaceWork.fileChanged', 'změna');
            return (
              <li key={`${f.status}:${f.path}`} className='flex items-baseline gap-2 text-[11px]'>
                <span className={cn(
                  'shrink-0 uppercase tracking-wide',
                  kind === 'new' && 'text-emerald-400',
                  kind === 'deleted' && 'text-red-400',
                  kind === 'renamed' && 'text-sky-400',
                  kind === 'changed' && 'text-amber-300',
                )}>
                  {label}
                </span>
                <span className='font-mono text-gray-300 truncate' title={f.path ?? ''}>
                  {f.path || '—'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {loading && (
        <div className='flex items-center gap-2 text-xs text-gray-500'>
          <Loader2 size={14} className='animate-spin' />
          {t('workspaceWork.diffLoading', 'Načítám změny…')}
        </div>
      )}
      {error && (
        <Alert variant='warning'>
          <span className='text-xs whitespace-pre-wrap'>{error}</span>
        </Alert>
      )}
      {!loading && !error && !diff && files.length === 0 && (
        <p className='text-xs text-gray-500'>
          {t('workspaceWork.diffEmpty', 'Žádné nezapsané změny.')}
        </p>
      )}
      {typeof diff === 'string' && diff.trim() !== '' && (
        <pre className='max-h-[55vh] overflow-auto rounded-lg bg-black/40 p-3 text-[11px] font-mono leading-relaxed'>
          {diff.split('\n').map((line, idx) => (
            <div
              key={idx}
              className={cn(
                line.startsWith('+++') || line.startsWith('---')
                  ? 'text-gray-400 font-semibold'
                  : line.startsWith('@@')
                  ? 'text-sky-400'
                  : line.startsWith('+')
                  ? 'bg-emerald-500/10 text-emerald-300'
                  : line.startsWith('-')
                  ? 'bg-red-500/10 text-red-300'
                  : line.startsWith('diff --git')
                  ? 'mt-2 text-gray-200'
                  : 'text-gray-400',
              )}
            >
              {line || ' '}
            </div>
          ))}
        </pre>
      )}
      {truncated && (
        <p className='text-[11px] text-amber-200/90'>
          {t('workspaceWork.diffTruncated', 'Zkráceno — je toho víc, než se sem vejde.')}
        </p>
      )}
      {ready && !loading && !error && files.length > 0 && (diff == null || diff.trim() === '') && (
        <p className='text-[11px] text-gray-500'>
          {t('workspaceWork.diffNamesOnly', 'Jsou vidět soubory. Samotný diff se nepodařilo načíst.')}
        </p>
      )}
    </div>
  );
}

function ParkedRow({
  item,
  fmtWhen,
  onMergeHelp,
}: {
  item: ParkedItem;
  fmtWhen: (iso?: string | null) => string | null;
  onMergeHelp?: () => void;
}) {
  const { t } = useTranslation();
  const unresolved = item.unresolved === true;
  const when = fmtWhen(item.parked_at);
  return (
    <li
      className={cn(
        'rounded-xl border px-3 py-2',
        unresolved
          ? 'border-red-500/40 bg-red-950/25'
          : 'border-gray-800/80 bg-gray-950/40',
      )}
    >
      <div className='flex items-start gap-2'>
        <span className={cn(
          'mt-1 h-2 w-2 shrink-0 rounded-full',
          unresolved ? 'bg-red-400' : item.current ? 'bg-plex' : 'bg-gray-500',
        )} />
        <div className='min-w-0 flex-1'>
          <div className='font-mono text-[11px] text-gray-200 truncate' title={item.branch ?? ''}>
            {shortenBranch(item.branch || '—')}
          </div>
          <div className='mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500'>
            <span>{item.owner || t('workspaceWork.unknownOwner', 'neznámý')}</span>
            {item.file_count != null && (
              <span>
                {t('workspaceWork.files', '{{count}} souborů', { count: item.file_count })}
              </span>
            )}
            {when && (
              <span>
                {t('workspaceWork.parkedAt', 'odloženo {{when}}', { when })}
              </span>
            )}
            {item.mine && (
              <Badge variant='info' className='text-[10px] py-0'>
                {t('workspaceWork.mine', 'tvoje')}
              </Badge>
            )}
          </div>
          {unresolved ? (
            <div className='mt-1 text-xs font-medium text-red-300'>
              {t('workspaceWork.unresolved', 'nerozhodnuto — odložila automatika')}
            </div>
          ) : item.defer_note ? (
            <div className='mt-1 text-xs text-gray-400'>„{item.defer_note}“</div>
          ) : item.reason ? (
            <div className='mt-1 text-xs text-gray-500'>{item.reason}</div>
          ) : null}
          {onMergeHelp && item.branch && (
            <button
              type='button'
              className='mt-1 text-[11px] font-medium text-plex hover:underline'
              onClick={onMergeHelp}
            >
              {t('workspaceWork.mergeHelp', 'pomoct sloučit')}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function BranchGraphView({
  baseName,
  baseCurrent,
  offshoots,
  compact,
  busy,
  onMerge,
  onRebase,
  onDefer,
  onMergeHelp,
}: {
  baseName: string;
  baseCurrent: boolean;
  offshoots: ParkedItem[];
  compact: boolean;
  busy: boolean;
  onMerge: (item: ParkedItem) => void;
  onRebase: (item: ParkedItem) => void;
  onDefer: (item: ParkedItem) => void;
  onMergeHelp?: (item: ParkedItem) => void;
}) {
  const { t } = useTranslation();
  const forkAt = useMemo(() => (offshoots.length ? 0.38 : 0.5), [offshoots.length]);

  return (
    <div className='space-y-1'>
      <div className='grid items-center gap-2' style={{ gridTemplateColumns: compact ? '5.5rem 1fr' : '8rem 1fr' }}>
        <div className='font-mono text-[11px] text-gray-300 truncate' title={baseName}>{baseName}</div>
        <div className='relative h-8'>
          <div className='absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-gray-700 via-gray-500 to-plex/70' />
          <span className='absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-gray-500' style={{ left: '18%' }} />
          <span className='absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-gray-400' style={{ left: `${forkAt * 100}%` }} />
          <span
            className={cn(
              'absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full',
              baseCurrent ? 'bg-emerald-400 ring-2 ring-emerald-400/30' : 'bg-gray-400',
            )}
            style={{ left: '88%' }}
          />
          {baseCurrent && (
            <span className='absolute right-0 top-full mt-0.5 text-[10px] uppercase tracking-wide text-emerald-400/80'>
              {t('workspaceWork.current', 'aktuální')}
            </span>
          )}
        </div>
      </div>

      {offshoots.map((item, idx) => {
        const tone = behindTone(item.commits_behind);
        const line = tone === 'danger'
          ? 'bg-red-500/70'
          : tone === 'warn'
          ? 'bg-amber-400/70'
          : 'bg-gray-500';
        const dot = tone === 'danger'
          ? 'bg-red-400'
          : tone === 'warn'
          ? 'bg-amber-400'
          : item.current
          ? 'bg-plex'
          : 'bg-gray-400';
        return (
          <div
            key={item.key || item.branch || idx}
            className='grid items-start gap-2 pt-2'
            style={{ gridTemplateColumns: compact ? '5.5rem 1fr' : '8rem 1fr' }}
          >
            <div
              className='font-mono text-[10px] text-gray-400 truncate pt-3'
              title={item.branch ?? ''}
            >
              {shortenBranch(item.branch || '—')}
            </div>
            <div className='relative min-w-0'>
              <div
                className={cn('absolute w-px', line)}
                style={{ left: `calc(${forkAt * 100}% )`, top: -10, height: 22 }}
              />
              <div
                className={cn('absolute h-px', line)}
                style={{ left: `calc(${forkAt * 100}%)`, width: 28, top: 12 }}
              />
              <div className='flex flex-wrap items-center gap-2 pl-10 py-1'>
                <span className={cn('h-2.5 w-2.5 rounded-full shrink-0', dot)} />
                <span className='text-[11px] text-gray-400'>
                  {item.file_count != null
                    ? t('workspaceWork.files', '{{count}} souborů', { count: item.file_count })
                    : '—'}
                  {item.age_days != null
                    ? ` · ${t('workspaceWork.days', '{{count}} dní', { count: item.age_days })}`
                    : ''}
                  {item.commits_behind != null
                    ? ` · ${t('workspaceWork.behind', '{{count}} commitů za {{base}}', {
                      count: item.commits_behind,
                      base: baseName,
                    })}`
                    : ''}
                </span>
                {tone !== 'ok' && (
                  <AlertTriangle
                    size={12}
                    className={tone === 'danger' ? 'text-red-400' : 'text-amber-400'}
                  />
                )}
                {item.owner && (
                  <span className='text-[10px] text-gray-600'>{item.owner}</span>
                )}
                {item.current && (
                  <Badge variant='info' className='text-[10px] py-0'>
                    {t('workspaceWork.current', 'aktuální')}
                  </Badge>
                )}
              </div>
              <div className='flex flex-wrap gap-1.5 pl-10 pt-1'>
                <Button
                  size='sm'
                  variant='success'
                  disabled={busy || !item.branch}
                  icon={<GitMerge size={12} />}
                  onClick={() => onMerge(item)}
                >
                  {t('workspaceWork.merge', 'Slít')}
                </Button>
                <Button
                  size='sm'
                  variant='secondary'
                  disabled={busy || !item.branch}
                  icon={<GitPullRequest size={12} />}
                  onClick={() => onRebase(item)}
                >
                  {t('workspaceWork.rebase', 'Přesadit')}
                </Button>
                <Button
                  size='sm'
                  variant='ghost'
                  disabled={busy || !item.branch}
                  icon={<PauseCircle size={12} />}
                  onClick={() => onDefer(item)}
                >
                  {t('workspaceWork.defer', 'Ponechat odložené')}
                </Button>
                {onMergeHelp && (
                  <Button
                    size='sm'
                    variant='ghost'
                    disabled={busy || !item.branch}
                    icon={<MessageSquare size={12} />}
                    onClick={() => onMergeHelp(item)}
                  >
                    {t('workspaceWork.mergeHelp', 'Pomoct sloučit')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {offshoots.length === 0 && (
        <p className='pt-2 text-xs text-gray-500'>
          {t('workspaceWork.graphEmpty', 'Žádná odbočka — všechno je na {{base}}.', { base: baseName })}
        </p>
      )}
    </div>
  );
}
